import { useContext, useEffect, useState } from "react";
import { Col, Row, Tabs } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import CompanyLeft from "../components/company-search/CompanyLeft";
import CompanyContent from "../components/company-search/CompanyContent";
import CompanyNavigation from "../components/company-search/CompanyNavigation";
import TrialModal from "../common/TrialModal";
import {
  emptyErrorObj,
  getCompanyList,
  resetQuickSelectedRow,
  saveAdvancedSelectedFilters,
  selectedRow,
} from "../actionCreator/companyListingActionCreater";
import "../assets/css/dynemic-page.css";
import { AuthContext } from "../context/AuthContext";
import { INDUSTRY_LIST_ERROR } from "../actionType/companyListingType";
import CompanyLogo from "../common/CompanyLogo";
import UserLogo from "../common/UserLogo";
import { Users } from "lucide-react";

const CompanySearch = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, collapsed } = useContext(AuthContext);
  const { isCompanyView, isLeadView, isExecutiveView } =
    useContext(AuthContext);
  const errObj = useSelector((state) => state.companyListingReducer.errObj);
  const selectedItem = useSelector((state) => state.HeaderReducer.selectedItem);
  const topSearchClick = useSelector(
    (state) => state.HeaderReducer.topSearchClick,
  );
  const topSearchValue = useSelector(
    (state) => state?.HeaderReducer?.topSearchValue,
  );
  const [activeTab, setActiveTab] = useState("1");
  const [isApiFailed, setIsApiFailed] = useState({
    isFailed: false,
    errObj: {},
  });

  useEffect(() => {
    if (!isCompanyView && isAuthenticated) {
      navigate("/dashboard-view");
    }
  }, [isCompanyView, navigate, isAuthenticated, searchParams]);

  useEffect(() => {
    const getCompanies = () => {
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }

      dispatch(
        getCompanyList({
          ...allParams,
          Name: topSearchValue,
          page: allParams?.page ? allParams?.page - 1 : 0,
        }),
      );
    };
    getCompanies();
  }, [dispatch, searchParams, topSearchValue, topSearchClick]);

  useEffect(() => {
    setIsApiFailed({ isFailed: !!Object.keys(errObj).length, errObj: errObj });
  }, [errObj]);

  const onChange = (key) => {
    setActiveTab(key);
    dispatch(selectedRow([]));
    if (key === "2") {
      navigate("/search-executive");
    }
    if (isAuthenticated && key === "3") {
      navigate("/search-lead");
    }
    dispatch(
      saveAdvancedSelectedFilters({
        quickSelectionRange: null,
      }),
    );
    dispatch(resetQuickSelectedRow([]));
  };

  useEffect(() => {
    if (location.pathname === "/search-company" && selectedItem === "Company") {
      setActiveTab("1");
    } else if (
      location.pathname === "/search-company" &&
      selectedItem === "Executive"
    ) {
      setActiveTab("2");
    }
  }, [topSearchClick, selectedItem, location.pathname]);

  const items = [
    {
      key: "1",
      isVisible: isAuthenticated ? isCompanyView : true,
      label: (
        <span className="d-flex align-items-center gap-10">
          <CompanyLogo />
          Companies
        </span>
      ),
      children: (
        <>
          <Row id="wrapper" className="tw-mt-2">
            <Col md={6} xl={5} xxl={4}>
              <CompanyLeft />
            </Col>
            <Col md={18} xl={19} xxl={20} className="tw-p-0">
              <CompanyContent />
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "2",
      isVisible: isAuthenticated ? isExecutiveView : true,
      label: (
        <span className="tw-flex tw-items-center gap-10">
          <UserLogo />
          Executive
        </span>
      ),
    },
    {
      key: "3",
      isVisible: isAuthenticated ? isLeadView : true,
      label: (
        <span className="d-flex align-items-center gap-10">
          <Users size={18} />
          Leads
        </span>
      ),
    },
  ];

  const closeModal = () => {
    dispatch(emptyErrorObj());
  };

  const redirectToSignup = () => {
    navigate("/signup");
  };

  const companySelectedFilterList = useSelector(
    (state) => state.companyListingReducer.selectedFilters,
  );
  const selectedRecords = useSelector(
    (state) => state.companyListingReducer.selectedRecords,
  );
  const companyFilterList = useSelector((state) => state.companyListingReducer);
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const itemsPerPage = parseInt(searchParams.get("size")) || 10;
  const totalItems = companyFilterList?.totalCount;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <>
      <div
        id="tabscompany"
        className={
          collapsed
            ? "tabs-extra-content-collapsed tw-px-4 tw-py-4 tw-h-[calc(100vh-64px)] tw-overflow-hidden"
            : `tabs-extra-content tw-px-4 tw-py-4 tw-h-[calc(100vh-64px)] tw-overflow-hidden`
        }
      >
        <Row className="tw-items-center tw-mb-2 tw-gap-y-2">
          <Col xs={24} md={10} lg={12}>
            <h2 className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-m-0">
              Data Search
            </h2>
            <p className="tw-text-slate-500 tw-text-xs tw-mt-1 tw-mb-0">
              Search and filter lead accounts, organizations, and industry-wide company datasets.
            </p>
          </Col>
          <Col
            xs={24}
            md={14}
            lg={12}
            className="tw-flex tw-items-center md:tw-justify-end tw-gap-2 tw-flex-wrap"
          >
            {companySelectedFilterList?.quickSelectionRange && (
              <span className="tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-medium tw-py-1 tw-px-3 tw-rounded-full">
                Quick Pages: {companySelectedFilterList?.quickSelectionRange?.start} - {companySelectedFilterList?.quickSelectionRange?.end}
              </span>
            )}
            {!!selectedRecords?.length && (
              <span className="tw-bg-[#EFEAFF] tw-text-[#5D44FF] tw-text-xs tw-font-semibold tw-py-1 tw-px-3 tw-rounded-full tw-shadow-sm">
                {selectedRecords?.length} records selected
              </span>
            )}
            {totalItems > 0 && (
              <span className="tw-bg-slate-100 tw-text-slate-600 tw-text-xs tw-font-medium tw-py-1 tw-px-3 tw-rounded-full">
                Showing {startItem} - {endItem} of {companyFilterList?.totalCount.toLocaleString()} results
              </span>
            )}
          </Col>
        </Row>
        <Tabs
          tabBarExtraContent={{
            right: <CompanyNavigation />,
          }}
          activeKey={activeTab}
          items={isAuthenticated ? items.slice(0, 2) : items}
          onChange={onChange}
        />
      </div>

      {isApiFailed.errObj[INDUSTRY_LIST_ERROR] ? (
        <TrialModal
          openModal={!!isApiFailed.errObj[INDUSTRY_LIST_ERROR]}
          closeModal={closeModal}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {isApiFailed.errObj[INDUSTRY_LIST_ERROR] &&
              isApiFailed.errObj[INDUSTRY_LIST_ERROR].message
                ? isApiFailed.errObj[INDUSTRY_LIST_ERROR].message
                : "Please call System support. You application having some issue."}
            </div>
          }
          modalWidth="400px"
        />
      ) : (
        ""
      )}
      <TrialModal
        openModal={!isAuthenticated && activeTab === "3"}
        closeModal={redirectToSignup}
        redirectToSignup={redirectToSignup}
        redirect={true}
        isSubscriptionMissing
      />
    </>
  );
};

export default CompanySearch;
